/* ============================================================
   ÉTAPE 11: DASHBOARD MARKETING & AFFICHAGE (Pro Max Final)
   ============================================================ */

import { 
  CheckCircle, 
  Clock, 
  DollarSign,
  Save, 
  Zap, 
  Sparkles,
  Info,
  Rocket,
  RefreshCw,
  Database,
  Layout as LayoutIcon,
  Search,
  ExternalLink
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../ui/button';
import { useDisplaySync } from '../../../../hooks/useDisplaySync';
import { BadgeMarketingEditor } from '../marketing/BadgeMarketingEditor';
import { TarifMarketingEditor } from '../marketing/TarifMarketingEditor';
import { ServiceMarketingEditor } from '../marketing/ServiceMarketingEditor';
import { ProcessMarketingEditor } from '../marketing/ProcessMarketingEditor';
import { PriceFromEditor } from '../marketing/PriceFromEditor';
import { BaseInfoSummary } from '../BaseInfoSummary';
import { PricingSummary } from '../PricingSummary';
import { WorkflowSummary } from '../WorkflowSummary';
import { SING_COLORS } from '../../../../constants/marketing-display.constants';

interface Step11MarketingProps {
  data: any;
  onChange: (updates: any) => void;
  onPublish?: () => void;
}

export function Step11Marketing({ data, onChange, onPublish }: Step11MarketingProps) {
  const isPublished = data.isPublic ?? false;
  const hasRequiredConfig = (data.capacityBrackets?.length || 0) > 0;
  const { syncDisplay } = useDisplaySync();
  const [isSyncing, setIsSyncing] = useState(false);

  const handleSync = async () => {
    setIsSyncing(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const displayData = syncDisplay(data);
    onChange({ 
      tarifGroups: displayData.tarifGroups,
      services: displayData.services,
      processSteps: displayData.processSteps,
      infoBadges: displayData.infoBadges,
      priceFromLabel: displayData.priceFromLabel,
      priceFromDetail: displayData.priceFromDetail,
    });
    setIsSyncing(false);
  };

  const updateDisplayField = (key: string, value: any) => {
    onChange({ [key]: value });
  };

  return (
    <div className="w-full space-y-10 animate-in fade-in duration-700">
      
      {/* --- PREMIUM HEADER --- */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white p-8 rounded-[32px] border-2 border-gray-50 shadow-sm relative overflow-hidden group">
        {/* Branding decoration */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-magenta-500/5 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-700" style={{ backgroundColor: `${SING_COLORS.MAGENTA}08` }} />
        
        <div className="flex items-center gap-6 relative z-10">
          <div className="w-20 h-20 rounded-[24px] bg-[#00758d] text-white flex items-center justify-center shadow-2xl shadow-[#00758d]/30 rotate-[-2deg]">
            <Rocket size={36} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider" style={{ backgroundColor: `${SING_COLORS.BLUE}15`, color: SING_COLORS.BLUE }}>Étape Finale</span>
              {isPublished && <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider" style={{ backgroundColor: `${SING_COLORS.MAGENTA}10`, color: SING_COLORS.MAGENTA }}>Public</span>}
            </div>
            <h2 className="text-3xl font-black text-[#00303c] tracking-tight">Console de Publication</h2>
            <p className="text-gray-400 font-medium mt-1">Donnez vie à votre configuration commerciale</p>
          </div>
        </div>

        <div className="flex items-center gap-4 relative z-10">
          <Button 
            type="button" 
            variant="ghost"
            onClick={() => {
              const clientBaseUrl = import.meta.env.VITE_CLIENT_URL || 'http://localhost:5173';
              window.open(`${clientBaseUrl}/accueil`, '_blank');
            }}
            className="h-14 px-6 rounded-2xl font-bold text-gray-400 hover:text-[#00758d] transition-all"
            disabled={!isPublished}
          >
            <ExternalLink size={20} className="mr-2" />
            Voir en ligne
          </Button>

          <Button 
            type="button"
            onClick={onPublish} 
            disabled={!hasRequiredConfig || isSyncing} 
            className="h-14 px-10 rounded-[20px] shadow-2xl font-black text-lg transition-all hover:scale-[1.03] active:scale-95 group text-white border-none"
            style={{ 
              backgroundColor: SING_COLORS.MAGENTA,
              boxShadow: `0 20px 40px ${SING_COLORS.MAGENTA}30`
            }}
          >
            <Save className="w-6 h-6 mr-3 group-hover:rotate-12 transition-transform" /> 
            {isPublished ? 'Mettre à jour' : 'Lancer la publication'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-10">
        
        {/* --- LEFT: SOURCE (TECH) --- */}
        <div className="col-span-12 xl:col-span-4 space-y-8 sticky top-8 h-fit">
          <div className="flex items-center justify-between px-2">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                <Database size={16} />
              </div>
              <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400">Source Technique</h3>
            </div>
            <div className="h-[1px] flex-1 bg-gray-100 ml-4"></div>
          </div>
          
          <div className="space-y-6">
            <BaseInfoSummary data={data} />
            <PricingSummary data={data} />
            <WorkflowSummary data={data} />
            
            <div className="p-6 rounded-[24px] border-2 border-dashed relative overflow-hidden group" style={{ backgroundColor: `${SING_COLORS.BURGUNDY}05`, borderColor: `${SING_COLORS.BURGUNDY}10`, color: SING_COLORS.BURGUNDY }}>
              <Search size={48} className="absolute -right-4 -bottom-4 opacity-5 group-hover:scale-110 transition-transform duration-500" />
              <p className="text-[11px] font-black uppercase tracking-wider mb-2 opacity-40">Aide à la rédaction</p>
              <p className="text-xs font-bold leading-relaxed relative z-10 opacity-70">
                La colonne de gauche contient vos réglages bruts. La colonne de droite permet de les "vendre" à vos clients avec des textes percutants.
              </p>
            </div>
          </div>
        </div>

        {/* --- RIGHT: DISPLAY (MARKETING) --- */}
        <div className="col-span-12 xl:col-span-8 space-y-12">
          <div className="flex items-center justify-between px-2">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${SING_COLORS.GOLD}15`, color: SING_COLORS.GOLD }}>
                <LayoutIcon size={16} />
              </div>
              <h3 className="text-xs font-black uppercase tracking-[0.2em]" style={{ color: SING_COLORS.GOLD }}>Contenus Marketing</h3>
            </div>
            <div className="flex items-center gap-4 flex-1 ml-4">
              <div className="h-[1px] flex-1 bg-gray-100"></div>
              <Button 
                type="button" 
                variant="outline"
                onClick={handleSync}
                className={`h-10 px-4 rounded-xl border-2 border-gray-100 font-bold text-xs transition-all ${isSyncing ? 'opacity-50' : 'hover:border-[#00758d]/30 hover:text-[#00758d]'}`}
                disabled={isSyncing}
              >
                {isSyncing ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
                Synchroniser avec la source
              </Button>
            </div>
          </div>

          {!data.tarifGroups ? (
            <div className="bg-white border-4 border-dashed border-gray-100 rounded-[40px] p-20 text-center space-y-6">
              <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto text-gray-200">
                <Sparkles size={48} />
              </div>
              <div className="max-w-md mx-auto">
                <h4 className="text-2xl font-black text-[#00303c]">Prêt pour la mise en forme ?</h4>
                <p className="text-gray-400 font-medium mt-3">Générez automatiquement une base de contenu marketing à partir de vos données techniques.</p>
              </div>
              <Button 
                type="button" 
                onClick={handleSync} 
                className="bg-[#00758d] hover:bg-[#00303c] rounded-[20px] px-10 h-14 font-black text-lg shadow-xl shadow-[#00758d]/20 transition-all"
              >
                Générer l'affichage automatique
              </Button>
            </div>
          ) : (
            <div className="space-y-16 pb-32 animate-in slide-in-from-bottom-8 duration-1000">
              
              <section className="space-y-6">
                <MarketingHeader title="Badge de prix d'appel" icon={DollarSign} color={SING_COLORS.BLUE} />
                <PriceFromEditor 
                  label={data.priceFromLabel || ''}
                  detail={data.priceFromDetail || ''}
                  onChange={(updates) => onChange(updates)}
                />
              </section>

              <section className="space-y-6">
                <MarketingHeader title="Badges & Bannières" icon={Info} color={SING_COLORS.MAGENTA} />
                <BadgeMarketingEditor 
                  badges={data.infoBadges || []} 
                  onChange={(val) => updateDisplayField('infoBadges', val)} 
                />
              </section>

              <section className="space-y-6">
                <MarketingHeader title="Configuration des Tarifs" icon={Zap} color={SING_COLORS.GOLD} />
                <TarifMarketingEditor 
                  groups={data.tarifGroups || []} 
                  onChange={(val) => updateDisplayField('tarifGroups', val)} 
                />
              </section>

              <section className="space-y-6">
                <MarketingHeader title="Services & Prestations" icon={CheckCircle} color={SING_COLORS.BLUE} />
                <ServiceMarketingEditor 
                  services={data.services || []} 
                  onChange={(val) => updateDisplayField('services', val)} 
                />
              </section>

              <section className="space-y-6">
                <MarketingHeader title="Comment ça marche ?" icon={Clock} color={SING_COLORS.BURGUNDY} />
                <ProcessMarketingEditor 
                  steps={data.processSteps || []} 
                  onChange={(val) => updateDisplayField('processSteps', val)} 
                />
              </section>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MarketingHeader({ title, icon: Icon, color }: { title: string, icon: any, color?: string }) {
  return (
    <div className="flex items-center gap-4 mb-8">
      <div className="w-12 h-12 rounded-2xl bg-white border-2 border-gray-50 flex items-center justify-center shadow-sm" style={{ color: color || '#00303c' }}>
        <Icon size={22} />
      </div>
      <h4 className="text-xl font-black text-[#00303c] tracking-tight">{title}</h4>
      <div className="h-[2px] flex-1 bg-gradient-to-r from-gray-100 to-transparent ml-2"></div>
    </div>
  );
}
